import * as Sentry from "@sentry/react";

import { sentryConfig } from "../env";

const { dsn } = sentryConfig;
if (dsn) {
  Sentry.init({
    dsn,
    environment: sentryConfig.environment,
    sendDefaultPii: false,
    // Console output elsewhere in the app can contain customer data.
    integrations: (defaults) => defaults.filter((integration) => integration.name !== "Breadcrumbs"),
  });
}

const isDev = process.env.NODE_ENV === "development";

// Sentry's per-envelope size ceiling is ~20MB; stay well under it so a large
// customer photo never blocks the rest of the event (tags/contexts/JSON diag).
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

const SECRET_KEY_PATTERN = /authorization|cookie|token|password|secret/i;

function sanitize(value, depth = 0) {
  if (value == null || depth > 4) return value;
  if (Array.isArray(value)) return value.map((v) => sanitize(v, depth + 1));
  if (typeof value === "object") {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      if (SECRET_KEY_PATTERN.test(key)) continue;
      out[key] = sanitize(val, depth + 1);
    }
    return out;
  }
  if (typeof value === "string" && value.length > 2000) {
    return `${value.slice(0, 2000)}…(truncated)`;
  }
  return value;
}

export function reportEvent(stage, data = {}) {
  if (!dsn) return;
  Sentry.captureMessage(stage, {
    level: "info",
    tags: { workflow_stage: stage },
    contexts: { upload: data },
  });
}

export function reportFailure(error, stage, data = {}) {
  if (!dsn) return;
  // Do not serialize Axios config/response or FormData containing customer data.
  const exception = new Error(`${stage} failed`);
  exception.name = error?.name || "UploadError";
  if (typeof error?.stack === "string") {
    exception.stack = `${exception.name}: ${exception.message}\n${error.stack.split("\n").slice(1).join("\n")}`;
  }
  Sentry.captureException(exception, {
    tags: { workflow_stage: stage },
    contexts: { upload: { ...data, status: error?.response?.status, error_code: error?.code } },
  });
}

export function newCorrelationId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_) {
    // fall through to the fallback id below
  }
  return `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Cheap, high-volume breadcrumbs for the stages of the upload flow. These are
// free (no event quota) and only surface when an error is later captured.
export function addUploadBreadcrumb(stage, data = {}) {
  if (!dsn) return;
  try {
    Sentry.addBreadcrumb({
      category: "image-upload",
      message: stage,
      level: "info",
      data: sanitize(data),
    });
  } catch (_) {
    // Observability must never break the upload flow.
  }
}

export function reportProductNotFound(details = {}) {
  if (!dsn) {
    if (isDev) console.warn("[product-resolution] product not found", details);
    return;
  }
  try {
    Sentry.captureMessage("product_resolution.not_found", {
      level: "warning",
      tags: { feature: "product-resolution" },
      contexts: { product: sanitize(details) },
    });
  } catch (_) {
    // Observability must never break rendering.
  }
}

export function isHeic(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return type === "image/heic" || type === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif");
}

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    if (!file || typeof Image === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
      reject(new Error("dimension reading unavailable in this environment"));
      return;
    }
    let objectUrl;
    try {
      objectUrl = URL.createObjectURL(file);
    } catch (err) {
      reject(err);
      return;
    }
    const cleanup = () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch (_) {
        // ignore
      }
    };
    const img = new Image();
    img.onload = () => {
      cleanup();
      resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
    };
    img.onerror = () => {
      cleanup();
      reject(new Error("image decoding failed while reading dimensions"));
    };
    img.src = objectUrl;
  });
}

// Never throws: dimension failures are recorded on the returned object instead.
export async function getImageMetadata(file, extra = {}) {
  const meta = {
    name: file?.name,
    mime_type: file?.type,
    extension: (file?.name || "").split(".").pop()?.toLowerCase(),
    size_bytes: file?.size,
    size_mb: typeof file?.size === "number" ? +(file.size / 1024 / 1024).toFixed(2) : undefined,
    last_modified: file?.lastModified,
    is_heic: isHeic(file),
    ...extra,
  };
  try {
    const { width, height } = await readImageDimensions(file);
    meta.width = width;
    meta.height = height;
    meta.megapixels = width && height ? +((width * height) / 1_000_000).toFixed(2) : undefined;
    meta.aspect_ratio = height ? +(width / height).toFixed(3) : undefined;
  } catch (err) {
    meta.dimension_read_failed = true;
    meta.dimension_read_error = err?.message;
  }
  return meta;
}

export function getClientContext() {
  try {
    const nav = typeof navigator !== "undefined" ? navigator : {};
    return {
      userAgent: nav.userAgent,
      platform: nav.platform,
      language: nav.language,
    };
  } catch (_) {
    return {};
  }
}

export function getNetworkContext() {
  try {
    const nav = typeof navigator !== "undefined" ? navigator : {};
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    return {
      online: nav.onLine,
      effectiveType: conn?.effectiveType,
      downlink: conn?.downlink,
      saveData: conn?.saveData,
    };
  } catch (_) {
    return {};
  }
}

// Reads a File into a Sentry attachment, defensively bailing out (never
// throwing) when the browser API is unavailable or the file is too large.
export async function buildImageAttachment(file, filename) {
  if (!file) return { attachment: null, skipped: true, reason: "no_file" };
  if (typeof file.arrayBuffer !== "function") {
    return { attachment: null, skipped: true, reason: "unsupported_file_api" };
  }
  if (typeof file.size === "number" && file.size > MAX_ATTACHMENT_BYTES) {
    return { attachment: null, skipped: true, reason: "too_large" };
  }
  try {
    const buffer = await file.arrayBuffer();
    return {
      attachment: {
        filename: filename || file.name || "upload.bin",
        data: new Uint8Array(buffer),
        contentType: file.type || "application/octet-stream",
      },
      skipped: false,
      reason: null,
    };
  } catch (_) {
    return { attachment: null, skipped: true, reason: "read_failed" };
  }
}

function buildDiagnosticAttachment(payload) {
  try {
    return {
      filename: "upload-debug.json",
      data: JSON.stringify(payload, null, 2),
      contentType: "application/json",
    };
  } catch (_) {
    return null;
  }
}

function extractAxiosErrorInfo(error, { method, url } = {}) {
  try {
    let sanitizedUrl = url;
    try {
      if (url && typeof window !== "undefined") {
        const parsed = new URL(url, window.location.origin);
        sanitizedUrl = `${parsed.origin}${parsed.pathname}`;
      }
    } catch (_) {
      // keep the raw url if it can't be parsed
    }
    return sanitize({
      name: error?.name,
      message: error?.message,
      code: error?.code,
      method,
      url: sanitizedUrl,
      has_response: !!error?.response,
      has_request: !!error?.request,
      status: error?.response?.status,
      status_text: error?.response?.statusText,
      response_content_type: error?.response?.headers?.["content-type"],
      response_data_preview:
        typeof error?.response?.data === "string"
          ? error.response.data.slice(0, 500)
          : undefined,
      timeout: error?.code === "ECONNABORTED",
    });
  } catch (_) {
    return { message: String(error?.message || error) };
  }
}

export { extractAxiosErrorInfo };

/**
 * Centralized capture for image-upload failures (HEIC conversion,
 * compression, storage, network upload). Creates ONE Sentry event, on a
 * local scope, so the original file / diagnostic JSON attached here never
 * leak onto unrelated events. Never throws.
 */
export async function captureImageUploadError({
  error,
  stage,
  originalFile,
  processedFile,
  product,
  correlationId,
  requestInfo = {},
  extra = {},
}) {
  const cid = correlationId || newCorrelationId();

  if (!dsn) {
    if (isDev) console.error("[IMAGE UPLOAD DEBUG]", stage, error, { correlationId: cid });
    return null;
  }

  try {
    const [imageMeta, attachmentResult] = await Promise.all([
      originalFile ? getImageMetadata(originalFile).catch(() => ({})) : Promise.resolve({}),
      originalFile
        ? buildImageAttachment(originalFile).catch(() => ({ attachment: null, skipped: true, reason: "unexpected_error" }))
        : Promise.resolve({ attachment: null, skipped: true, reason: "no_file" }),
    ]);

    const processedMeta = processedFile
      ? { name: processedFile.name, mime_type: processedFile.type, size_bytes: processedFile.size }
      : undefined;

    const diagnosticPayload = sanitize({
      timestamp: new Date().toISOString(),
      correlationId: cid,
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
      stage,
      product,
      original_image: imageMeta,
      processed_image: processedMeta,
      request: requestInfo,
      client: getClientContext(),
      network: getNetworkContext(),
      extra,
    });

    let eventId = null;
    Sentry.withScope((scope) => {
      scope.setTag("feature", "image-upload");
      scope.setTag("upload.stage", stage);
      if (product?.type) scope.setTag("upload.product", product.type);
      if (imageMeta.mime_type) scope.setTag("upload.file_type", imageMeta.mime_type);
      scope.setTag("upload.heic", !!imageMeta.is_heic);
      scope.setTag("upload.compressed", !!processedMeta);
      if (requestInfo.status !== undefined) scope.setTag("upload.http_status", String(requestInfo.status));
      scope.setTag("upload.failure_type", error?.code || error?.name || "unknown");

      scope.setContext("image", imageMeta);
      if (processedMeta) scope.setContext("upload_processed", processedMeta);
      scope.setContext("upload", { correlationId: cid, stage, endpoint: requestInfo.url, status: requestInfo.status });
      if (product) scope.setContext("product", sanitize(product));
      scope.setContext("network", getNetworkContext());
      scope.setContext("client", getClientContext());

      let attachmentAdded = false;
      if (attachmentResult?.attachment) {
        scope.addAttachment(attachmentResult.attachment);
        attachmentAdded = true;
      } else if (attachmentResult?.skipped && attachmentResult.reason !== "no_file") {
        scope.setTag("upload.attachment_skip_reason", attachmentResult.reason);
      }
      const diagnosticAttachment = buildDiagnosticAttachment(diagnosticPayload);
      if (diagnosticAttachment) scope.addAttachment(diagnosticAttachment);
      scope.setTag("upload.attachment_added", attachmentAdded);

      const exception = error instanceof Error ? error : new Error(String(error?.message || error || stage));
      eventId = Sentry.captureException(exception);
    });

    if (isDev) {
      console.groupCollapsed(`[IMAGE UPLOAD DEBUG] ${stage}`);
      console.log("correlationId", cid);
      console.log("diagnostic", diagnosticPayload);
      console.log("sentryEventId", eventId);
      console.groupEnd();
    }

    return eventId;
  } catch (captureError) {
    console.error("Sentry capture failed", captureError);
    return null;
  }
}
